/**
 * TOSS Web Application - Enums
 * TypeScript enums matching .NET 9 backend models
 * Based on PROJECT_DOCUMENTATION.md Section 20
 */

/**
 * User roles in the system (11 roles)
 * Corresponds to user_roles enum in the database
 */
export enum UserRole {
  SuperAdmin = 'SuperAdmin',
  Mechanic = 'Mechanic',
  Intern = 'Intern',
  Administrator = 'Administrator',
  Accountant = 'Accountant',
  Chef = 'Chef',
  HeadOfFinancialAndMaterialProvision = 'HeadOfFinancialAndMaterialProvision',
  HeadOfDispatchingService = 'HeadOfDispatchingService',
  HeadOfPTO = 'HeadOfPTO',
  Dispatcher = 'Dispatcher',
  /**
   * Заказчик — an employee of a CUSTOMER company, using the external client portal
   * (`/api/client/*`, `Policies.ClientAccess`). The system's first non-staff audience:
   * scoped to their own company's data, never touches the permission matrix, never
   * reaches `/api/lk/*` or `/api/mobile/*`. See `INTERNAL_ROLES` in `constants/roles.ts`
   * — every staff-role dropdown must derive from that list, not from `UserRole` directly,
   * or this role leaks into worker-management UI as a selectable staff role.
   */
  Client = 'Client',
}

/**
 * User account status
 * Corresponds to users_status enum in the database
 */
export enum UserStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Blocked = 'Blocked',
}

/**
 * Application (work order) priority levels
 * Corresponds to applications_priority enum in the database
 */
export enum ApplicationPriority {
  WithoutPriority = 'WithoutPriority',
  Medium = 'Medium',
  High = 'High',
  EmergencyRepair = 'EmergencyRepair',
}

/**
 * Application (work order) status
 * Corresponds to applications_status enum in the database
 */
export enum ApplicationStatus {
  /**
   * Новая - создана клиентом в ЛК, ждёт приёмки диспетчером.
   * «Липкий» статус: калькулятор статусов его не трогает, единственный выход —
   * POST /api/lk/applications/{id}/accept (право applications.accept).
   */
  New = 'New',
  /** В работе у Механика - есть неосмотренные PTO */
  WorkMechanic = 'WorkMechanic',
  /** В работе у ФМО - все PTO осмотрены, ждут верификации */
  WorkFmo = 'WorkFmo',
  /** В работе у Бухгалтера - все PTO верифицированы, ждут оплаты */
  WorkAccountant = 'WorkAccountant',
  /** Завершена - все PTO оплачены */
  Completed = 'Completed',
  /** Отменена */
  Cancel = 'Cancel',
  /** В архиве */
  Archive = 'Archive',
}

/**
 * PTO inspection status within an application
 * Corresponds to applications_pto_status enum in the database
 */
export enum ApplicationPtoStatus {
  NotExamined = 'NotExamined',
  NotCompleted = 'NotCompleted',
  PartiallyCompleted = 'PartiallyCompleted',
  Completed = 'Completed',
}

/**
 * PTO payment status in the verification workflow
 * Corresponds to pto_payment_status enum in the database
 */
export enum PtoPaymentStatus {
  Pending = 'Pending',
  Verified = 'Verified',
  AwaitingPayment = 'AwaitingPayment',
  Paid = 'Paid',
  Overdue = 'Overdue',
}

/**
 * Expense approval status
 * Corresponds to expenses_status enum in the database
 */
export enum ExpenseStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

/**
 * Task execution status
 * Corresponds to tasks_status enum in the database
 */
export enum TaskStatus {
  Work = 'Work',
  Completed = 'Completed',
  Cancel = 'Cancel',
}

/**
 * Task priority levels
 * Corresponds to tasks_priority enum in the database
 */
export enum TaskPriority {
  WithoutPriority = 'WithoutPriority',
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

/**
 * Item type for inspection (container or equipment)
 * Corresponds to item_type enum in the database
 */
export enum ItemType {
  Container = 'Container',
  Equipment = 'Equipment',
}

/**
 * Payment terms for customers
 * Corresponds to payment_term_type enum in the database
 */
export enum PaymentTerm {
  Days3 = 'Days3',
  Week1 = 'Week1',
  Weeks2 = 'Weeks2',
  Month1 = 'Month1',
}

/**
 * Notification types
 */
export enum NotificationType {
  Application = 'Application',
  Task = 'Task',
  Expense = 'Expense',
  Other = 'Other',
  Message = 'Message',
}

/**
 * Driver's license type
 * Corresponds to drivers_license_type enum in the database
 */
export enum DriversLicenseType {
  Domestic = 'Domestic',
  International = 'International',
}

/**
 * Equipment type categories
 * Corresponds to equipment_type enum in the database
 */
export enum EquipmentType {
  /** ДГК - Diesel Generator Container */
  DGK = 'DGK',
  /** ЭГК - Electric Generator Container */
  EGK = 'EGK',
  /** АРВ - Automatic Voltage Regulator */
  ARV = 'ARV',
  /** Genset - Generator Set */
  Genset = 'Genset',
}

// `ContainerType` / `ContainerSize` (closed two-value enums mirroring the old `container_type` /
// `container_size` DB enums) were removed for good (inspection redesign, Phase 4). Values come
// exclusively from the `directory.container_attributes.*` directory now — `useContainerTypesList()`
// / `useContainerSizesList()` (`features/directory/container-attributes/hooks/`) on the internal
// side, and a per-domain literal on the client portal (`features/client/components/fleet/
// clientFleetFormat.ts`'s `KNOWN_CONTAINER_SIZE_CODES`, which has no directory access). The wire
// value stays a plain `string` everywhere — a container's `type`/`size` field, computed
// server-side from the directory row.

/**
 * Account deletion request status
 * Corresponds to accounts_deletion_requests_status enum in the database
 */
export enum AccountDeletionRequestStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

/**
 * Service cost type (for whom the cost applies)
 * Corresponds to services_cost_type enum in the database
 */
export enum ServicesCostType {
  Mechanic = 'Mechanic',
  Client = 'Client',
}

/**
 * Service type (for which items it applies)
 * Corresponds to service_type enum in the database
 */
export enum ServiceType {
  Container = 'Container',
  Equipment = 'Equipment',
  General = 'General',
}

/**
 * Education level
 * Corresponds to education_level enum in the database
 */
export enum EducationLevel {
  Higher = 'Высшее',
  SecondaryProfessional = 'Среднее профессиональное',
}

/**
 * Instructions categories
 * Corresponds to instructions_categories enum in the database
 */
export enum InstructionsCategory {
  Applications = 'Заявки',
  Tasks = 'Задачи',
  Expenses = 'Расходы',
  Reports = 'Отчеты',
  Repair = 'Ремонт',
  Emergencies = 'Чрезвычайные ситуации',
}

// ============================================================================
// Display labels for enums (Russian localization)
// ============================================================================

export const UserRoleLabels: Record<UserRole, string> = {
  [UserRole.SuperAdmin]: 'Суперадмин',
  [UserRole.Mechanic]: 'Механик',
  [UserRole.Intern]: 'Стажёр',
  [UserRole.Administrator]: 'Администратор',
  [UserRole.Accountant]: 'Бухгалтер',
  [UserRole.Chef]: 'Начальник',
  [UserRole.HeadOfFinancialAndMaterialProvision]: 'Начальник ФМО',
  [UserRole.HeadOfDispatchingService]: 'Начальник диспетчерской',
  [UserRole.HeadOfPTO]: 'Начальник ПТО',
  [UserRole.Dispatcher]: 'Диспетчер',
  // Deliberately not "Клиент" — that word already means the customer/counterparty
  // entity in this app's UI (directory.customers, "Клиенты"). "Заказчик" is the term
  // the backend role comment and the client-portal spec use for this role specifically.
  [UserRole.Client]: 'Заказчик',
};

/**
 * Reason codes for an inspection that was not completed.
 *
 * Mirrors `NotCompletedReasons` in `src/Toss.Api/Data/Enums/NotCompletedReasons.cs`. This is the
 * TYPED half of the reason story and is therefore labelled here, client-side. The sibling PROSE
 * field (`reason` / `reasonLabel`) is labelled server-side by the same C# class — do not run
 * `reason` through this map, it is free text and the lookup would return `undefined`.
 */
export type NotCompletedReason =
  | 'NoTime'
  | 'BrokenTool'
  | 'WastedTrip'
  | 'Overslept'
  | 'NoAccess'
  | 'Other';

/**
 * Russian labels for {@link NotCompletedReason}. Kept in sync by hand with
 * `NotCompletedReasons.Labels` on the server (the project forbids an i18n library); both sets of
 * literals are pinned by tests on either side.
 */
export const NotCompletedReasonLabels: Record<NotCompletedReason, string> = {
  NoTime: 'Не хватило времени',
  BrokenTool: 'Сломался инструмент',
  WastedTrip: 'Холостой выезд',
  Overslept: 'Проспал',
  NoAccess: 'Нет прохода',
  Other: 'Другая причина',
};

export const UserStatusLabels: Record<UserStatus, string> = {
  [UserStatus.Pending]: 'Ожидает рассмотрения',
  [UserStatus.Approved]: 'Одобрен',
  [UserStatus.Rejected]: 'Отклонён',
  [UserStatus.Blocked]: 'Заблокирован',
};

/**
 * Откуда заявка пришла.
 *
 * Зеркало `Data/Enums/ApplicationSource.cs`. Внутренней аудитории источник отдаётся
 * перечислением — в отличие от кабинета, который получает готовую строку и знает только про
 * письмо. Диспетчеру различать все три нужно: плашка ожидания приёмки печатала «из личного
 * кабинета клиента» для любой новой заявки, включая разобранную из письма.
 */
export enum ApplicationSource {
  /** Заведена сотрудником внутри TOSS. Этим же значением засеяны все строки до Р6. */
  Staff = 0,
  /** Создана заказчиком в кабинете. */
  Portal = 1,
  /** Разобрана из письма почтовым шлюзом. */
  Email = 2,
}

export const ApplicationSourceLabels: Record<ApplicationSource, string> = {
  [ApplicationSource.Staff]: 'Заведена сотрудником',
  [ApplicationSource.Portal]: 'Из личного кабинета',
  [ApplicationSource.Email]: 'Из письма',
};

export const ApplicationPriorityLabels: Record<ApplicationPriority, string> = {
  [ApplicationPriority.WithoutPriority]: 'Без приоритета',
  [ApplicationPriority.Medium]: 'Обычный',
  [ApplicationPriority.High]: 'Высокий',
  [ApplicationPriority.EmergencyRepair]: 'Срочный',
};

export const ApplicationStatusLabels: Record<ApplicationStatus, string> = {
  // Byte-identical to ApplicationsTableColumns.StatusEnumValues on the backend — the
  // DataTable badge reads the server's enumValues, these maps feed the filter panels.
  [ApplicationStatus.New]: 'Новая',
  [ApplicationStatus.WorkMechanic]: 'В работе (Механик)',
  [ApplicationStatus.WorkFmo]: 'В работе (ФМО)',
  [ApplicationStatus.WorkAccountant]: 'В работе (Бухгалтер)',
  [ApplicationStatus.Completed]: 'Завершена',
  [ApplicationStatus.Cancel]: 'Отменена',
  [ApplicationStatus.Archive]: 'В архиве',
};

export const ApplicationPtoStatusLabels: Record<ApplicationPtoStatus, string> = {
  [ApplicationPtoStatus.NotExamined]: 'ПТО не рассмотрен',
  [ApplicationPtoStatus.NotCompleted]: 'Осмотр ПТО не выполнен',
  [ApplicationPtoStatus.PartiallyCompleted]: 'Осмотр ПТО частично выполнен',
  [ApplicationPtoStatus.Completed]: 'Осмотр ПТО выполнен',
};

export const PtoPaymentStatusLabels: Record<PtoPaymentStatus, string> = {
  [PtoPaymentStatus.Pending]: 'На проверке ФМО',
  [PtoPaymentStatus.Verified]: 'Проверен ФМО',
  [PtoPaymentStatus.AwaitingPayment]: 'Ожидает оплаты',
  [PtoPaymentStatus.Paid]: 'Оплачен',
  [PtoPaymentStatus.Overdue]: 'Просрочен',
};

export const ExpenseStatusLabels: Record<ExpenseStatus, string> = {
  [ExpenseStatus.Pending]: 'Ожидает рассмотрения',
  [ExpenseStatus.Approved]: 'Одобрен',
  [ExpenseStatus.Rejected]: 'Отклонён',
};

export const TaskStatusLabels: Record<TaskStatus, string> = {
  [TaskStatus.Work]: 'В работе',
  [TaskStatus.Completed]: 'Завершена',
  [TaskStatus.Cancel]: 'Отменена',
};

export const TaskPriorityLabels: Record<TaskPriority, string> = {
  [TaskPriority.WithoutPriority]: 'Без приоритета',
  [TaskPriority.Low]: 'Низкий',
  [TaskPriority.Medium]: 'Обычный',
  [TaskPriority.High]: 'Высокий',
};

export const ItemTypeLabels: Record<ItemType, string> = {
  [ItemType.Container]: 'Контейнер',
  [ItemType.Equipment]: 'Энергетическая установка',
};

export const PaymentTermLabels: Record<PaymentTerm, string> = {
  [PaymentTerm.Days3]: '3 дня',
  [PaymentTerm.Week1]: '1 неделя',
  [PaymentTerm.Weeks2]: '2 недели',
  [PaymentTerm.Month1]: '1 месяц',
};

export const NotificationTypeLabels: Record<NotificationType, string> = {
  [NotificationType.Application]: 'Заявка',
  [NotificationType.Task]: 'Задача',
  [NotificationType.Expense]: 'Расход',
  [NotificationType.Other]: 'Прочее',
  [NotificationType.Message]: 'Упоминание',
};

export const DriversLicenseTypeLabels: Record<DriversLicenseType, string> = {
  [DriversLicenseType.Domestic]: 'Национальные',
  [DriversLicenseType.International]: 'Международные',
};

export const EquipmentTypeLabels: Record<EquipmentType, string> = {
  [EquipmentType.DGK]: 'ДГК',
  [EquipmentType.EGK]: 'ЭГК',
  [EquipmentType.ARV]: 'АРВ',
  [EquipmentType.Genset]: 'Дженсет',
};

export const AccountDeletionRequestStatusLabels: Record<AccountDeletionRequestStatus, string> = {
  [AccountDeletionRequestStatus.Pending]: 'Ожидает рассмотрения',
  [AccountDeletionRequestStatus.Approved]: 'Одобрен',
  [AccountDeletionRequestStatus.Rejected]: 'Отклонён',
};

export const InstructionsCategoryLabels: Record<InstructionsCategory, string> = {
  [InstructionsCategory.Applications]: 'Заявки',
  [InstructionsCategory.Tasks]: 'Задачи',
  [InstructionsCategory.Expenses]: 'Расходы',
  [InstructionsCategory.Reports]: 'Отчеты',
  [InstructionsCategory.Repair]: 'Ремонт',
  [InstructionsCategory.Emergencies]: 'Чрезвычайные ситуации',
};

export const ServiceTypeLabels: Record<ServiceType, string> = {
  [ServiceType.Container]: 'Контейнер',
  [ServiceType.Equipment]: 'Энергетическая установка',
  [ServiceType.General]: 'Общие',
};

/**
 * Human-readable RU labels for the seeded English service-category codes.
 * ServiceCategory is a free-form string in the DB, so any unmapped value
 * (custom or already-Russian, e.g. «Общие») falls back to the raw value.
 */
export const ServiceCategoryLabels: Record<string, string> = {
  Repair: 'Ремонт',
  Cleaning: 'Уборка',
  Maintenance: 'Обслуживание',
  Inspection: 'Осмотр',
  Logistics: 'Логистика',
  Documentation: 'Документация',
  Supply: 'Снабжение',
  Bulk: 'Навалочные грузы',
  Misc: 'Прочее',
  Other: 'Прочее',
};

/** RU label for a service-category code, or the raw value when unmapped. */
export const serviceCategoryLabel = (category: string): string =>
  ServiceCategoryLabels[category] ?? category;

// ============================================================================
// Vision v4 Phase 2 — Service tarification and auto-add triggers
// ============================================================================

/**
 * Service tarification model
 * Fixed: mechanic payment is a fixed amount per service
 * NormHours: mechanic payment = NormHours × PTO.NormHourCostMechanic
 */
export enum ServiceTarification {
  Fixed = 'Fixed',
  NormHours = 'NormHours',
}

/**
 * Trigger for automatically adding a service to PTO inspection
 * None: not auto-added
 * KrkSmallCount: container count < 6
 * KrkUpTo20: 6 ≤ container count ≤ 20
 * KrkOver20: container count > 20
 * PtoInTransitFirst: PTO type is InTransit
 * PtoOnTerminalFirst: PTO type is OnTerminal
 */
export enum ServiceAutoAddTrigger {
  None = 'None',
  KrkSmallCount = 'KrkSmallCount',
  KrkUpTo20 = 'KrkUpTo20',
  KrkOver20 = 'KrkOver20',
  PtoInTransitFirst = 'PtoInTransitFirst',
  PtoOnTerminalFirst = 'PtoOnTerminalFirst',
}

export const ServiceTarificationLabels: Record<ServiceTarification, string> = {
  [ServiceTarification.Fixed]: 'Фиксированная',
  [ServiceTarification.NormHours]: 'По нормо-часам',
};

export const ServiceAutoAddTriggerLabels: Record<ServiceAutoAddTrigger, string> = {
  [ServiceAutoAddTrigger.None]: 'Не добавлять',
  [ServiceAutoAddTrigger.KrkSmallCount]: 'КРК < 6',
  [ServiceAutoAddTrigger.KrkUpTo20]: 'КРК 6–20',
  [ServiceAutoAddTrigger.KrkOver20]: 'КРК > 20',
  [ServiceAutoAddTrigger.PtoInTransitFirst]: 'ПТО «в пути»',
  [ServiceAutoAddTrigger.PtoOnTerminalFirst]: 'ПТО «на терминале»',
};
